/**
 * Wave 5 SEO cluster: file naming, layout traps, education, follow-up, Mexico.
 * Spanish and English. Merged from posts.js. Mid CTA variant `mid`.
 */

export default [
  {
    slug: 'nombre-archivo-cv',
    date: '2026-08-22',
    category: 'format',
    related: ['enviar-cv-por-email', 'checklist-cv-antes-de-enviar', 'cv-pdf-vs-word'],
    es: {
      title: 'Cómo nombrar el archivo del CV (PDF que sí abren)',
      description:
        'Nombre de archivo que RR. HH. encuentra: cv-nombre-apellido-puesto.pdf. Sin FINAL2, tildes raras ni espacios. ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'El nombre del archivo del CV es lo primero que ve quien descarga el adjunto. “curriculum_final_FINAL2.pdf” o “CV.pdf” se pierde en una carpeta de cincuenta descargas. Tampoco ayuda “José_María_Ñíguez currículum!!.pdf”: algunos portales recortan tildes, espacios y signos. Si ya tienes el PDF, no hace falta reescribir la vida. Cambia el nombre antes de enviarlo.',
        },
        {
          type: 'p',
          text: 'Patrón que funciona en España y en la mayoría de ATS: cv-nombre-apellido-puesto.pdf. Minúsculas, guiones, sin espacios. El puesto es corto y copiado de la oferta (“product-analyst”, “auxiliar-administrativo”), no un eslogan. Hermanas: [enviar el CV por email](/blog/enviar-cv-por-email) y [checklist antes de enviar](/blog/checklist-cv-antes-de-enviar). [PDF o Word](/blog/cv-pdf-vs-word) decide el formato; esta guía decide cómo se llama el fichero.',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com) regenera un PDF A4 parseable. Al descargar, renombra tú el archivo con tu nombre real. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        { type: 'h2', text: 'La fórmula (y lo que tira el portal)' },
        {
          type: 'p',
          text: 'Antes: CV.pdf, mi_cv_v7, “Currículum Vitae José”. Después: cv-ana-ruiz-data-analyst.pdf. Quien abre el zip de InfoJobs o el buzón de Gmail ve quién eres y a qué puesto. El ATS a veces guarda el filename como metadato; un “documento1” no aporta keyword, pero un nombre limpio tampoco la sustituye: las keywords van en el texto del CV.',
        },
        {
          type: 'ul',
          items: [
            'Usa cv- o resume- al inicio si aplicas en inglés.',
            'Nombre y un apellido (dos si el primero es muy común).',
            'Puesto en 1–3 palabras, en el idioma del anuncio.',
            'Evita tildes y ñ si el portal es antiguo; “niguez” es feo pero llega. En email a una persona, “Nuñez” suele ir bien.',
            'Nada de “definitivo”, fechas raras, ni el nombre de la empresa destino en el archivo (se ve si reenvías el mismo PDF a otra).',
          ],
        },
        { type: 'h2', text: 'Espacios, mayúsculas y el límite de caracteres' },
        {
          type: 'p',
          text: 'Algunos formularios de Workday o de ayuntamiento recortan a 50–80 caracteres o rechazan espacios. Un nombre de 120 caracteres con el título completo del máster se parte. Acorta. Las mayúsculas no suman profesionalidad al filename: CV-ANA-RUIZ.PDF parece un grito. Minúsculas y guiones se leen igual en Windows, Mac y el móvil del reclutador.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El archivo se llama CV.pdf. Tú no',
          lead: 'Si el PDF es ilegible o de dos columnas, el nombre no salva el filtro. Sube el fichero, regenera plantilla A4 y luego renómbralo.',
          from: 'CV.pdf / FINAL2',
          to: 'cv-nombre-puesto.pdf',
          button: 'Preparar el PDF y nombrarlo',
        },
        { type: 'h2', text: 'Un archivo por oferta (sin volverte loco)' },
        {
          type: 'p',
          text: 'Si adaptas el CV, el filename puede llevar el puesto, no la empresa. Guardas cv-ana-ruiz-data-analyst.pdf y cv-ana-ruiz-bi-analyst.pdf. No clones veinte PDFs “para Mercadona” en el nombre: te equivocarás de adjunto. El contenido sí se [adapta a la oferta](/blog/adaptar-cv-a-una-oferta); el nombre solo identifica.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Pongo la fecha en el nombre?',
              a: 'No hace falta. “cv-ana-ruiz-2026-08” envejece en una semana y no ayuda al reclutador.',
            },
            {
              q: '¿Y si el anuncio pide Word?',
              a: 'Misma fórmula con .docx. Sigue sin espacios. Ver PDF vs Word.',
            },
            {
              q: '¿ClarCV nombra el archivo por mí?',
              a: 'Descargas un PDF limpio. El nombre del fichero lo pones tú al guardar.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'How to name your CV file (a PDF they will open)',
      description:
        'A filename HR can find: cv-firstname-lastname-role.pdf. No FINAL2, odd accents, or spaces. ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'The CV filename is the first thing someone sees after they download the attachment. “curriculum_final_FINAL2.pdf” or “CV.pdf” vanishes in a folder of fifty files. “José_María_Ñíguez resume!!.pdf” is not better: some portals strip accents, spaces, and symbols. If you already have the PDF, you do not rewrite your life. Rename the file before you send it.',
        },
        {
          type: 'p',
          text: 'A pattern that works in Spain and in most ATS tools: cv-firstname-lastname-role.pdf. Lowercase, hyphens, no spaces. The role is short and copied from the ad (“product-analyst”), not a slogan. Sisters: [send the CV by email](/blog/enviar-cv-por-email) and the [pre-send checklist](/blog/checklist-cv-antes-de-enviar). [PDF vs Word](/blog/cv-pdf-vs-word) is the format; this guide is the filename.',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com) regenerates a parseable A4 PDF. After download, rename the file with your real name. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        { type: 'h2', text: 'The formula (and what portals reject)' },
        {
          type: 'p',
          text: 'Before: CV.pdf, my_cv_v7, “Curriculum Vitae Jose”. After: cv-ana-ruiz-data-analyst.pdf. Whoever opens the InfoJobs zip or the Gmail inbox sees who you are and which role. ATS may store the filename as metadata; “document1” adds no keyword, but a clean name does not replace keywords in the body.',
        },
        {
          type: 'ul',
          items: [
            'Start with cv- or resume- if the ad is in English.',
            'First name and one last name (two if the first is very common).',
            'Role in 1–3 words, in the language of the ad.',
            'Avoid accents and ñ on old portals; ugly “niguez” still arrives. In a human email, “Nunez” is usually fine.',
            'No “final”, odd dates, or the destination company in the filename (it shows if you reuse the PDF).',
          ],
        },
        { type: 'h2', text: 'Spaces, caps, and character limits' },
        {
          type: 'p',
          text: 'Some Workday or city-hall forms truncate at 50–80 characters or reject spaces. A 120-character name with a full master title gets cut. Shorten it. Caps do not make a filename more professional: CV-ANA-RUIZ.PDF looks like a shout. Lowercase and hyphens read the same on Windows, Mac, and a phone.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'The file is called CV.pdf. You are not',
          lead: 'If the PDF is unreadable or two-column, the name will not save the parse. Upload, regenerate an A4 template, then rename.',
          from: 'CV.pdf / FINAL2',
          to: 'cv-name-role.pdf',
          button: 'Prepare the PDF and name it',
        },
        { type: 'h2', text: 'One file per role (without going mad)' },
        {
          type: 'p',
          text: 'If you tailor the CV, the filename can carry the role, not the company. Keep cv-ana-ruiz-data-analyst.pdf and cv-ana-ruiz-bi-analyst.pdf. Do not clone twenty PDFs “for Company X” in the name: you will attach the wrong one. Content is [tailored to the ad](/blog/adaptar-cv-a-una-oferta); the name only identifies.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I put the date in the name?',
              a: 'No need. “cv-ana-ruiz-2026-08” ages in a week and does not help the recruiter.',
            },
            {
              q: 'What if the ad asks for Word?',
              a: 'Same formula with .docx. Still no spaces. See PDF vs Word.',
            },
            {
              q: 'Does ClarCV name the file for me?',
              a: 'You download a clean PDF. You set the filename when you save.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-a-dos-columnas',
    date: '2026-08-22',
    category: 'format',
    related: ['cv-canva-y-ats', 'plantillas-cv-compatibles-ats', 'iconos-en-el-cv'],
    es: {
      title: 'CV a dos columnas: cuándo el ATS lo rompe',
      description:
        'El CV de dos columnas se ve bien en pantalla y se parte en Workday. Prueba del bloc de notas y cómo pasar a una columna. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Un CV a dos columnas encaja en una página y parece “de diseñador”. El ATS no ve columnas: ve el orden de las capas. A menudo lee el sidebar (skills, iconos, fechas) antes que tu nombre, o mezcla el puesto de la izquierda con las fechas de la derecha. Quien busca “cv dos columnas” suele tener un Canva o un Word con tabla. El trabajo no es defender el diseño. Es decidir si ese PDF va a un portal.',
        },
        {
          type: 'p',
          text: 'Regla corta: portal o ATS (InfoJobs, Greenhouse, SuccessFactors) → una columna, encabezados Experiencia / Formación / Habilidades. Email a una persona o portfolio creativo → dos columnas pueden valer, y aun así conviene un gemelo parseable. Guías: [Canva y ATS](/blog/cv-canva-y-ats), [plantillas compatibles](/blog/plantillas-cv-compatibles-ats), [iconos en el CV](/blog/iconos-en-el-cv).',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com): subes el PDF de dos columnas y regeneras plantilla A4 de una. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. No es un score mágico contra Workday.',
        },
        { type: 'h2', text: 'La prueba de dos minutos' },
        {
          type: 'p',
          text: 'Abre el PDF. Selecciona desde el nombre hasta la tercera viñeta. Pega en el bloc de notas. Si el email sale en medio de un puesto, si las fechas se desordenan o faltan frases, no envíes ese archivo al formulario. Una tabla de Word “que parece dos columnas” falla igual: el parser recorre celdas, no el ojo humano.',
        },
        {
          type: 'ul',
          items: [
            'Sidebar oscura + cuerpo: el texto del margen suele ir primero o no ir.',
            'Fechas en una columna y títulos en otra: el ATS fusiona dos empleos.',
            'Skills solo en el lateral con barras: a veces no existen para el filtro.',
            'Encabezado a todo el ancho y luego dos columnas: mejor que dos columnas desde la línea 1, pero sigue siendo frágil.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Dos columnas no son dos lecturas',
          lead: 'Tú ves un diseño. El parser ve un río de cajas. Sube el PDF y deja Experiencia en una sola columna de texto.',
          from: 'Tabla / sidebar Canva',
          to: 'Una columna A4',
          button: 'Pasar mi CV a una columna',
        },
        { type: 'h2', text: 'Cómo aplanar el archivo que ya tienes' },
        {
          type: 'p',
          text: 'No redibujes en Canva “un poco menos estrecho”. Extrae el texto (copia del PDF o del editor) y colócalo en orden: contacto, resumen, experiencia, formación, skills, idiomas. Las fechas van junto al puesto, no en un raíl izquierdo. Si el CV se alarga, recorta viñetas; no vuelvas a la tabla para “que quepa”. Ver [CV demasiado largo](/blog/cv-demasiado-largo).',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿RR. HH. odia las dos columnas?',
              a: 'Un humano puede leerlas. El software del primer filtro, a menudo no. El problema es el portal, no el gusto del recruiter.',
            },
            {
              q: '¿Europass de dos columnas cuenta?',
              a: 'Europass “bonito” con recuadros falla igual. El Europass clásico de una columna parsea mejor. Ver Europass vs moderno.',
            },
            {
              q: '¿ClarCV detecta columnas?',
              a: 'Señala fricción de estructura. Tú eliges una plantilla de una columna. No “hackea” Canva.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Two-column CV: when ATS breaks it',
      description:
        'A two-column CV looks fine on screen and splits in Workday. Notepad test and how to flatten to one column. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'A two-column CV fits one page and looks “designed”. ATS tools do not see columns: they see layer order. They often read the sidebar (skills, icons, dates) before your name, or merge a left-side title with right-side dates. People searching this usually have Canva or a Word table. The job is not to defend the layout. It is to decide whether that PDF goes into a portal.',
        },
        {
          type: 'p',
          text: 'Short rule: portal or ATS (job board, Greenhouse, SuccessFactors) → one column, headings Experience / Education / Skills. Human email or creative portfolio → two columns can work, and you still want a parseable twin. Guides: [Canva and ATS](/blog/cv-canva-y-ats), [ATS templates](/blog/plantillas-cv-compatibles-ats), [icons on the CV](/blog/iconos-en-el-cv).',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com): upload the two-column PDF and regenerate a one-column A4 template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. Not a magic Workday score.',
        },
        { type: 'h2', text: 'The two-minute test' },
        {
          type: 'p',
          text: 'Open the PDF. Select from the name through the third bullet. Paste into Notepad. If the email lands in the middle of a job, dates scramble, or sentences vanish, do not send that file to the form. A Word table that “looks like two columns” fails the same way: the parser walks cells, not the human eye.',
        },
        {
          type: 'ul',
          items: [
            'Dark sidebar + body: margin text often comes first or not at all.',
            'Dates in one column and titles in the other: ATS merges two jobs.',
            'Skills only in the side rail with bars: sometimes they do not exist for the filter.',
            'Full-width header then two columns: better than two columns from line 1, still fragile.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Two columns are not two readings',
          lead: 'You see a layout. The parser sees a river of boxes. Upload the PDF and keep Experience in one text column.',
          from: 'Table / Canva sidebar',
          to: 'One A4 column',
          button: 'Flatten my CV to one column',
        },
        { type: 'h2', text: 'How to flatten the file you already have' },
        {
          type: 'p',
          text: 'Do not redraw in Canva “a bit less narrow”. Extract the text (copy from the PDF or the editor) and place it in order: contact, summary, experience, education, skills, languages. Dates sit next to the role, not on a left rail. If the CV grows, cut bullets; do not return to the table to “make it fit”. See [CV too long](/blog/cv-demasiado-largo).',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Do recruiters hate two columns?',
              a: 'A human can read them. The first-filter software often cannot. The problem is the portal, not taste.',
            },
            {
              q: 'Does a two-column Europass count?',
              a: 'A “pretty” boxed Europass fails the same way. Classic one-column Europass parses better.',
            },
            {
              q: 'Does ClarCV detect columns?',
              a: 'It flags structure friction. You pick a one-column template. It does not “hack” Canva.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-infografico',
    date: '2026-08-22',
    category: 'format',
    related: ['cv-canva-y-ats', 'iconos-en-el-cv', 'errores-ats-que-descartan-tu-cv'],
    es: {
      title: 'CV infográfico: por qué el portal no lo lee',
      description:
        'Líneas de tiempo, iconos y porcentajes no son keywords. Cuándo un CV infográfico vale y cómo tener un gemelo ATS. ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'Un CV infográfico (timeline, círculo de “creatividad 90%”, iconos de herramientas) es un póster. El ATS no indexa un dibujo de Photoshop ni una barra de Excel. Quien busca esto suele venir de Canva o de un “CV creativo” de Pinterest. Si el archivo va a InfoJobs, LinkedIn Easy Apply o Workday, el infográfico es el documento equivocado. Si va a un estudio de diseño por email, puede ser muestra — no el único archivo.',
        },
        {
          type: 'p',
          text: 'No vas a “hacer el infográfico más ATS”. Vas a separar: un PDF parseable para el filtro y, si el oficio lo pide, un portfolio o un one-pager visual. Hermanas: [Canva y ATS](/blog/cv-canva-y-ats), [iconos](/blog/iconos-en-el-cv), [errores ATS](/blog/errores-ats-que-descartan-tu-cv).',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com) no convierte un póster en otro póster. Extrae lo que se pueda leer y lo pasa a plantilla A4. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        { type: 'h2', text: 'Qué se pierde en el parseo' },
        {
          type: 'ul',
          items: [
            'Logos de Figma, SAP o Meta sin la palabra escrita.',
            'Años en una línea de tiempo gráfica sin “2021–2024” en texto.',
            'Foto + formas que empujan el email fuera del flujo.',
            'QR al portfolio: el parser no lo sigue; pon la URL en contacto.',
            'Infografía de “soft skills” al 80%: no es evidencia ni keyword.',
          ],
        },
        {
          type: 'p',
          text: 'Antes: un A4 con flechas, tres colores y “liderazgo” en un pictograma. Después: las mismas fechas y logros en viñetas, herramientas nombradas, enlace a Behance en texto. El segundo entra al ATS. El primero, a veces, solo enseña un nombre.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El timeline no es una fecha',
          lead: 'Si tus años son una raya dibujada, el filtro no extrae 2023. Sube el PDF y deja fechas y tools en palabras.',
          from: 'Póster Canva',
          to: 'Fechas y stack en texto',
          button: 'Convertir el infográfico',
        },
        { type: 'h2', text: 'Cuándo sí enviar el visual' },
        {
          type: 'p',
          text: 'Dirección de arte, ilustración, motion, a veces marketing creativo: un humano pidió “CV creativo” o portfolio. Adjunta el parseable al formulario y el visual (o la URL) donde quepa. Un solo PDF infográfico en un ATS corporativo es la peor pareja. Si el visual es el único archivo que tienes, extrae el texto a mano o súbelo a ClarCV y revisa que el modelo no invente un puesto.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Los CVs infográficos “destacan”?',
              a: 'Destacan en una mesa. En un parser, desaparecen. Destacar después del filtro, no en lugar del filtro.',
            },
            {
              q: '¿Puedo dejar un icono si también escribo la skill?',
              a: 'El icono sobra para el ATS. Si el humano lo ve después, no pasa nada; la palabra tiene que estar.',
            },
            {
              q: '¿ClarCV redibuja infografías?',
              a: 'No. Regenera un CV de texto. El portfolio visual lo sigues enlazando tú.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Infographic CV: why the job board cannot read it',
      description:
        'Timelines, icons, and percentages are not keywords. When an infographic CV is fine and how to keep an ATS twin. ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'An infographic CV (timeline, “creativity 90%” circle, tool icons) is a poster. ATS does not index a Photoshop drawing or an Excel bar. People searching this usually come from Canva or a Pinterest “creative CV”. If the file goes to a job board, Easy Apply, or Workday, the infographic is the wrong document. If it goes to a design studio by email, it can be a sample — not the only file.',
        },
        {
          type: 'p',
          text: 'You will not “make the infographic more ATS”. You will split: a parseable PDF for the filter and, if the craft needs it, a portfolio or visual one-pager. Sisters: [Canva and ATS](/blog/cv-canva-y-ats), [icons](/blog/iconos-en-el-cv), [ATS mistakes](/blog/errores-ats-que-descartan-tu-cv).',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com) does not turn a poster into another poster. It extracts what can be read and puts it on an A4 template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        { type: 'h2', text: 'What the parse drops' },
        {
          type: 'ul',
          items: [
            'Figma, SAP, or Meta logos without the word written out.',
            'Years on a graphic timeline with no “2021–2024” in text.',
            'Photo + shapes that push the email out of the flow.',
            'QR to the portfolio: the parser does not follow it; put the URL in contact.',
            'An 80% “soft skills” chart: not proof and not a keyword.',
          ],
        },
        {
          type: 'p',
          text: 'Before: an A4 of arrows, three colours, and “leadership” as a pictogram. After: the same dates and wins as bullets, tools named, Behance link in text. The second file enters ATS. The first sometimes only shows a name.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'The timeline is not a date',
          lead: 'If your years are a drawn line, the filter will not extract 2023. Upload the PDF and keep dates and tools in words.',
          from: 'Canva poster',
          to: 'Dates and stack in text',
          button: 'Convert the infographic',
        },
        { type: 'h2', text: 'When the visual is worth sending' },
        {
          type: 'p',
          text: 'Art direction, illustration, motion, sometimes creative marketing: a human asked for a “creative CV” or a portfolio. Attach the parseable file to the form and the visual (or URL) where it fits. A single infographic PDF in a corporate ATS is the worst pair. If the visual is the only file you have, extract text by hand or upload to ClarCV and check the model did not invent a job.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Do infographic CVs “stand out”?',
              a: 'They stand out on a table. In a parser, they vanish. Stand out after the filter, not instead of it.',
            },
            {
              q: 'Can I keep an icon if I also write the skill?',
              a: 'The icon is unused by ATS. If a human sees it later, fine; the word must be there.',
            },
            {
              q: 'Does ClarCV redraw infographics?',
              a: 'No. It regenerates a text CV. You still link the visual portfolio.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'voluntariado-en-el-cv',
    date: '2026-08-22',
    category: 'writing',
    related: ['cv-sin-experiencia', 'primer-empleo-cv', 'intereses-en-el-cv'],
    es: {
      title: 'Voluntariado en el CV: cuándo cuenta como experiencia',
      description:
        'ONG, Cruz Roja o un festival: cómo escribir el voluntariado con fechas y logros, no como hobby. ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'El voluntariado en el CV cuenta cuando hubo rol, fechas y un resultado. “Me gusta ayudar” no. Quien busca esto suele ser junior, cambio de sector o un hueco que no quiere dejar vacío. No es lo mismo coordinar turnos en Cruz Roja dos años que un sábado de recogida de basura. El primero es experiencia. El segundo, si acaso, una línea en intereses.',
        },
        {
          type: 'p',
          text: 'Trátalo como un puesto: organización, función, periodo, 2–3 viñetas con verbo y hecho. El ATS busca Volunteer, Voluntariado, el nombre de la ONG y skills (logística, atención, captación). Guías: [CV sin experiencia](/blog/cv-sin-experiencia), [primer empleo](/blog/primer-empleo-cv), [intereses](/blog/intereses-en-el-cv) para no mezclar hobbies con trabajo no remunerado serio.',
        },
        {
          type: 'p',
          text: 'Si el bloque ya está en tu PDF pero es una frase vaga, reescríbelo y pasa el archivo por ClarCV (clarcv.com) para que “Voluntariado” sea un encabezado de texto. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        { type: 'h2', text: 'Dónde colocarlo' },
        {
          type: 'p',
          text: 'Si es tu bloque más fuerte (sin contrato aún, o el único liderazgo reciente), ponlo dentro de Experiencia o justo debajo, no al pie junto a “cine y viajar”. Si ya tienes tres empleos sólidos, una sección Voluntariado corta al final basta. No dupliques el mismo rol en experiencia e intereses.',
        },
        {
          type: 'ul',
          items: [
            'Nombre legal o conocido de la entidad (no solo “una ONG”).',
            'Ciudad o remoto, y horas solo si son altas y estables (“4 h/semana, 2023–2025”).',
            'Logro: turnos cubiertos, fondos, personas atendidas, campaña — cifras honestas.',
            'Keywords del destino: si aplicas a eventos, el festival cuenta más que un voluntariado genérico.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '“Me gusta ayudar” no es una viñeta',
          lead: 'Si el voluntariado es un icono o una línea huérfana, el ATS no extrae Cruz Roja ni logística. Sube el PDF y deja entidad y fechas en texto.',
          from: 'Hobby vago',
          to: 'Rol + fechas + hecho',
          button: 'Reescribir el voluntariado',
        },
        { type: 'h2', text: 'Lo que no pongas' },
        {
          type: 'p',
          text: 'Un día suelto, “voluntario de corazón”, política de partido si no es el sector, o inventar horas. En entrevista pueden pedir un contacto de la entidad. Si el voluntariado tapa un [hueco laboral](/blog/hueco-laboral-en-el-cv), dilo con fechas reales, no como si fuera un full-time que no fue.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿El voluntariado sustituye un empleo?',
              a: 'No en salario. Sí como prueba de hábitos y skills si no hay contrato. Un junior con un año de coordinación en asociación pesa más que un CV vacío.',
            },
            {
              q: '¿Pongo “no remunerado”?',
              a: 'El nombre Voluntariado ya lo dice. No hace falta disculparte.',
            },
            {
              q: '¿ClarCV inventa cifras de la ONG?',
              a: 'No debe. Tú pegas las que puedas defender. El modelo formatea.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Volunteering on a CV: when it counts as experience',
      description:
        'NGO, Red Cross, or a festival: how to write volunteering with dates and outcomes, not as a hobby. ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'Volunteering on a CV counts when there was a role, dates, and an outcome. “I like helping” does not. People searching this are often junior, changing sector, or filling a gap. Coordinating Red Cross shifts for two years is not the same as one Saturday of litter picking. The first is experience. The second is, at most, a line under interests.',
        },
        {
          type: 'p',
          text: 'Treat it as a job: organisation, function, period, 2–3 bullets with a verb and a fact. ATS looks for Volunteer, the NGO name, and skills (logistics, front-of-house, fundraising). Guides: [CV with no experience](/blog/cv-sin-experiencia), [first job](/blog/primer-empleo-cv), [interests](/blog/intereses-en-el-cv) so you do not mix hobbies with serious unpaid work.',
        },
        {
          type: 'p',
          text: 'If the block is already in your PDF but vague, rewrite it and run the file through ClarCV (clarcv.com) so “Volunteering” is a text heading. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        { type: 'h2', text: 'Where to place it' },
        {
          type: 'p',
          text: 'If it is your strongest block (no contract yet, or the only recent leadership), put it inside Experience or just below, not at the bottom next to “films and travel”. If you already have three solid jobs, a short Volunteering section at the end is enough. Do not duplicate the same role in experience and interests.',
        },
        {
          type: 'ul',
          items: [
            'Legal or well-known name of the organisation (not just “an NGO”).',
            'City or remote, and hours only if they are high and stable (“4 h/week, 2023–2025”).',
            'Outcome: shifts covered, funds, people supported, a campaign — honest numbers.',
            'Keywords for the target: applying to events, a festival counts more than generic volunteering.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '“I like helping” is not a bullet',
          lead: 'If volunteering is an icon or an orphan line, ATS will not extract Red Cross or logistics. Upload the PDF and keep org and dates in text.',
          from: 'Vague hobby',
          to: 'Role + dates + fact',
          button: 'Rewrite the volunteering',
        },
        { type: 'h2', text: 'What to leave out' },
        {
          type: 'p',
          text: 'A one-off day, “volunteer at heart”, party politics unless that is the sector, or invented hours. In interview they may ask for a contact at the organisation. If volunteering covers an [employment gap](/blog/hueco-laboral-en-el-cv), say so with real dates, not as a full-time job it was not.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Does volunteering replace a job?',
              a: 'Not in pay. Yes as proof of habits and skills if there is no contract. A junior with a year of association coordination beats an empty CV.',
            },
            {
              q: 'Should I write “unpaid”?',
              a: 'The heading Volunteering already says it. No need to apologise.',
            },
            {
              q: 'Will ClarCV invent NGO figures?',
              a: 'It should not. You paste numbers you can defend. The model formats.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'nota-media-en-el-cv',
    date: '2026-08-22',
    category: 'writing',
    related: ['primer-empleo-cv', 'cv-para-practicas', 'cv-sin-experiencia'],
    es: {
      title: 'Nota media en el CV: cuándo ponerla y cuándo no',
      description:
        'Expediente, 7,8/10 o GPA: cuándo la nota media ayuda (junior, prácticas) y cuándo sobra. ClarCV.',
      readMinutes: 10,
      sections: [
        {
          type: 'p',
          text: 'La nota media en el CV solo vale si es un imán o si la oferta la pide. Un 5,4/10 no convence a nadie; un 8,7 en un grado técnico, a un junior sí. Quien busca esto suele estar en prácticas, primer empleo o máster. Si ya tienes tres años de contrato, el expediente pasa a segundo plano: pesan los logros. No clones una guía de “cómo hacer el CV”: decide si el número que ya tienes merita una línea.',
        },
        {
          type: 'p',
          text: 'En España se entiende /10. Si aplicas fuera, aclara la escala: “8,2/10 (notable)”. El GPA estadounidense no es 1:1; no conviertas a ojo un 7,1 en un 3.5. Hermanas: [primer empleo](/blog/primer-empleo-cv), [CV para prácticas](/blog/cv-para-practicas), [sin experiencia](/blog/cv-sin-experiencia).',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com) no inventa un 9. Conserva la nota que está en el archivo o la que tú editas. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        { type: 'h2', text: 'Cuándo sí (y cómo escribirla)' },
        {
          type: 'p',
          text: 'Prácticas, becas, graduate programmes, consultoras que filtran por expediente, o una oferta que dice “nota mínima 7”. Ponla junto al título, no en un recuadro gráfico: Grado en Física, UAM, 2024 — nota media 8,4/10. Si solo una asignatura es excelente y el resto no, no fingas media: “TFG 9,5/10” es más honesto.',
        },
        {
          type: 'ul',
          items: [
            'Umbral útil en España: suele merecer la línea a partir de ~7,5–8, salvo que pidan otra cifra.',
            'Matrícula de honor o premio: una frase, no un escudo.',
            'FP: la nota también cuenta en grado superior si es alta; no la escondas por “no ser universidad”.',
            'Posgrado: la media del máster si es reciente y el grado no.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Un 5,9 no se arregla con un icono',
          lead: 'Si la nota es un sello decorativo o no está en texto, quítala o escríbela junto al título. Sube el PDF y deja Formación limpia.',
          from: 'Sello / barra de nota',
          to: '8,4/10 junto al grado',
          button: 'Limpiar la formación',
        },
        { type: 'h2', text: 'Cuándo quitarla' },
        {
          type: 'p',
          text: 'Media mediocre, título de hace una década y carrera senior, o un número que no puedes demostrar si piden el certificado. Tampoco pongas todas las notas por asignatura. El ATS no puntúa “estadística 6”. Si el bloque de formación es un muro, recorta: ver longitud y [mejorar el CV que ya tienes](/blog/mejorar-el-cv-que-ya-tienes).',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Pongo el expediente completo?',
              a: 'No. Media o un premio. El certificado se adjunta si lo piden (oposiciones, becas).',
            },
            {
              q: '¿Nota de selectividad / EvAU?',
              a: 'Casi nunca, salvo becas de grado muy junior. El grado o la FP pesan más.',
            },
            {
              q: '¿ClarCV calcula la media?',
              a: 'No. Tú pones el número oficial.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'GPA or average grade on a CV: when to show it',
      description:
        'Transcript, 8.2/10, or GPA: when the average helps (junior, internships) and when to drop it. ClarCV.',
      readMinutes: 10,
      sections: [
        {
          type: 'p',
          text: 'An average grade on a CV is worth a line if it is a magnet or the ad asks for it. A 5.4/10 convinces no one; an 8.7 in a technical degree can help a junior. People searching this are usually in internships, a first job, or a master. After three years on contract, the transcript is secondary: outcomes weigh more. Do not clone a “how to write a CV” guide: decide whether the number you already have deserves a line.',
        },
        {
          type: 'p',
          text: 'In Spain, /10 is understood. Abroad, state the scale: “8.2/10 (notable)”. A US GPA is not 1:1; do not eyeball a 7.1 into a 3.5. Sisters: [first job](/blog/primer-empleo-cv), [internship CV](/blog/cv-para-practicas), [no experience](/blog/cv-sin-experiencia).',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com) does not invent a 9. It keeps the grade in the file or the one you edit. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        { type: 'h2', text: 'When to include it (and how)' },
        {
          type: 'p',
          text: 'Internships, scholarships, graduate programmes, consultancies that filter on grades, or an ad that says “minimum 7”. Put it next to the degree, not in a graphic badge: BSc Physics, UAM, 2024 — average 8.4/10. If only one course was excellent, do not fake an average: “thesis 9.5/10” is more honest.',
        },
        {
          type: 'ul',
          items: [
            'Useful bar in Spain: the line is usually worth it from ~7.5–8, unless they asked another number.',
            'Honours or a prize: one sentence, not a crest.',
            'Vocational training: a high grade still counts; do not hide it because it is not university.',
            'Postgrad: the master average if it is recent and the bachelor is not.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'A 5.9 is not fixed by an icon',
          lead: 'If the grade is a decorative stamp or not in text, drop it or write it next to the degree. Upload the PDF and keep Education clean.',
          from: 'Stamp / grade bar',
          to: '8.4/10 next to the degree',
          button: 'Clean up education',
        },
        { type: 'h2', text: 'When to remove it' },
        {
          type: 'p',
          text: 'A mediocre average, a decade-old degree plus a senior career, or a number you cannot prove if they ask for the certificate. Do not list every module mark. ATS does not score “statistics 6”. If education is a wall, cut it: see length and [improve the CV you already have](/blog/mejorar-el-cv-que-ya-tienes).',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I attach the full transcript?',
              a: 'No. Average or one prize. Attach the certificate if they ask (public exams, grants).',
            },
            {
              q: 'University-entrance score?',
              a: 'Almost never, except very junior degree grants. The degree or vocational diploma weighs more.',
            },
            {
              q: 'Does ClarCV compute the average?',
              a: 'No. You enter the official number.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'fp-en-el-cv',
    date: '2026-08-22',
    category: 'career',
    related: ['primer-empleo-cv', 'cursos-online-en-el-cv', 'cv-sin-experiencia'],
    es: {
      title: 'FP en el CV: grado medio, superior y FCT',
      description:
        'Cómo escribir un grado medio o superior, la FCT y la FP dual sin esconderlas ni inflarlas. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'La FP en el CV no es un “plan B” que hay que disimular. Un grado superior de DAM, administración o electromecánica es el título que piden muchas ofertas en España. El error caro es escribir “estudios de informática” sin el nombre oficial, o esconder la FCT como si no fuera experiencia. Quien busca esto ya tiene el título o lo está terminando. Reescribe el bloque, no inventes un grado universitario.',
        },
        {
          type: 'p',
          text: 'Pon el nombre completo del ciclo (y la familia profesional si aclara), centro, años, y si es dual. La FCT (formación en centros de trabajo) va como experiencia si hubo tareas reales: empresa, meses, 2 viñetas. No la mezcles con un [curso Udemy](/blog/cursos-online-en-el-cv). Hermanas: [primer empleo](/blog/primer-empleo-cv), [sin experiencia](/blog/cv-sin-experiencia).',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com): subes el Word o PDF y dejas Formación y Experiencia en encabezados que el ATS mapea. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        { type: 'h2', text: 'Cómo nominar el título' },
        {
          type: 'p',
          text: 'Antes: “FP de informático”. Después: “Técnico Superior en Desarrollo de Aplicaciones Multiplataforma (DAM), IES …, 2023–2025. FP dual en …”. El ATS y el reclutador de pyme buscan DAM, DAW, ASIR, GAD, GI, electromecánica — las siglas y el nombre largo, al menos una vez. Si el ciclo es LOE o LOGSE antiguo, el nombre oficial sigue siendo la keyword, no “bachillerato técnico”.',
        },
        {
          type: 'ul',
          items: [
            'Grado medio: no lo hinches a superior. El nivel se verifica.',
            'Grado superior + universidad a medias: pon ambos; no borres la FP si es lo que te contrata.',
            'Módulos relevantes (2–4) solo si el anuncio los nombra; no listes los quince.',
            'Nota media: solo si es alta; ver la guía de nota media.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '“Informático” no es un ciclo',
          lead: 'Si el título es un icono o un eufemismo, el filtro no extrae DAM ni FCT. Sube el PDF y deja el nombre oficial en texto.',
          from: '“Estudios de informática”',
          to: 'Técnico Superior en DAM',
          button: 'Corregir Formación y FCT',
        },
        { type: 'h2', text: 'FCT y FP dual como experiencia' },
        {
          type: 'p',
          text: 'Tres meses en empresa con tickets o mostrador son experiencia. Escribe el nombre de la empresa, no solo “prácticas del instituto”. Si la dual fue contrato, dilo. Si aún no hay FCT, no inventes la empresa. Competencias (soldadura, facturación, React) van en skills y en viñetas, no en un recuadro de iconos.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿La FP “queda mal” frente a un grado?',
              a: 'En oficios que piden ciclo, el grado de menos no gana. En ofertas de universidad, la FP no sobra si hay experiencia o un stack real.',
            },
            {
              q: '¿Pongo ESO o bachillerato si tengo superior?',
              a: 'Suele sobrar. El título más alto basta, salvo oposiciones que pidan la cadena.',
            },
            {
              q: '¿ClarCV cambia FP por “Bachelor”?',
              a: 'No debe traducir un ciclo a un grado. Revisa el inglés si exportas.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Vocational training (FP) on a CV: intermediate, higher, internships',
      description:
        'How to write a Spanish ciclo (grado medio/superior), FCT work placement, and dual FP without hiding or inflating it. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Vocational training on a CV is not a “plan B” to hide. A higher ciclo in DAM, administration, or electromechanics is what many Spanish ads ask for. The expensive mistake is writing “IT studies” without the official name, or hiding the FCT placement as if it were not experience. People searching this already have the diploma or are finishing it. Rewrite the block; do not invent a university degree.',
        },
        {
          type: 'p',
          text: 'Use the full programme name (and the professional family if it clarifies), school, years, and whether it was dual. FCT (workplace training) belongs under experience if you did real tasks: company, months, 2 bullets. Do not mix it with a [Udemy course](/blog/cursos-online-en-el-cv). Sisters: [first job](/blog/primer-empleo-cv), [no experience](/blog/cv-sin-experiencia).',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com): upload the Word or PDF and keep Education and Experience as headings ATS can map. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        { type: 'h2', text: 'How to name the diploma' },
        {
          type: 'p',
          text: 'Before: “vocational IT”. After: “Higher Technician in Multi-platform App Development (DAM), IES …, 2023–2025. Dual FP at …”. ATS and SME recruiters search DAM, DAW, ASIR, and the long name at least once. If the ciclo is an older LOE/LOGSE title, the official name is still the keyword, not “technical baccalaureate”.',
        },
        {
          type: 'ul',
          items: [
            'Intermediate (grado medio): do not inflate it to higher. The level is checked.',
            'Higher ciclo plus a partial university degree: list both; do not delete FP if that is what gets you hired.',
            'Relevant modules (2–4) only if the ad names them; do not list all fifteen.',
            'Average grade: only if it is high; see the grade guide.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '“IT person” is not a ciclo',
          lead: 'If the title is an icon or a euphemism, the filter will not extract DAM or FCT. Upload the PDF and keep the official name in text.',
          from: '“IT studies”',
          to: 'Higher Technician in DAM',
          button: 'Fix Education and FCT',
        },
        { type: 'h2', text: 'FCT and dual FP as experience' },
        {
          type: 'p',
          text: 'Three months in a company with tickets or a counter is experience. Write the company name, not only “school placement”. If dual FP was a contract, say so. If FCT has not happened yet, do not invent the company. Skills (welding, invoicing, React) go in skills and bullets, not in an icon box.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Does FP look weak next to a bachelor?',
              a: 'For roles that ask for a ciclo, a weaker bachelor does not win. For university-only ads, FP still helps if you have experience or a real stack.',
            },
            {
              q: 'List ESO or bachillerato if I have a higher ciclo?',
              a: 'Usually no. The highest title is enough, unless a public exam asks for the chain.',
            },
            {
              q: 'Will ClarCV turn FP into “Bachelor”?',
              a: 'It should not. Check the English export.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-para-oposiciones',
    date: '2026-08-22',
    category: 'career',
    related: ['checklist-cv-antes-de-enviar', 'que-no-poner-en-el-cv', 'cv-para-profesor'],
    es: {
      title: 'CV para oposiciones: el archivo que pide la convocatoria',
      description:
        'Méritos, PDF y formulario: qué CV enviar a una oposición (y qué no inventar). No somos el BOE. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Un CV para oposiciones no gana el examen. A veces ni siquiera es el documento que puntúa: la convocatoria pide instancia, titulación y baremo de méritos en su modelo. Quien busca esto suele tener un Canva de empresa y quiere “adaptarlo a la Administración”. El riesgo es enviar un infográfico donde piden un PDF simple, o un CV creativo donde piden anexos numerados. Lee la convocatoria. ClarCV no es el BOE ni un gestor de oposiciones.',
        },
        {
          type: 'p',
          text: 'Cuando sí piden currículum (bolsas, interinidades, algunos procesos autonómicos, entrevistas tras el examen): una columna, fechas exactas, títulos con denominación oficial, nada de foto si no la piden, nada de DNI en el PDF si el formulario ya lo lleva. Hermanas: [qué no poner](/blog/que-no-poner-en-el-cv), [checklist](/blog/checklist-cv-antes-de-enviar), [PDF vs Word](/blog/cv-pdf-vs-word).',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com) deja el archivo parseable y A4. Tú cruzas cada línea con el baremo. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. No calculamos puntos de mérito.',
        },
        { type: 'h2', text: 'CV vs instancia vs méritos' },
        {
          type: 'p',
          text: 'Son tres cosas. La instancia es el formulario oficial. Los méritos son cursos, antigüedad y títulos según bases. El CV es un resumen narrativo que a veces piden “para conocimiento del tribunal” o para una bolsa. No copies el diseño de una academia de oposiciones: copia los campos que piden las bases. Si piden “currículum en modelo oficial”, ese PDF manda, no una plantilla de LinkedIn.',
        },
        {
          type: 'ul',
          items: [
            'Títulos: nombre oficial, centro, fecha de expedición. Un máster “propio” no es un máster oficial.',
            'Cursos: horas y entidad. Un webinar de 45 minutos rara vez barema.',
            'Experiencia: mes y año, empleador, funciones que el temario o la plaza reconocen.',
            'No pongas objetivos creativos ni barras de “vocación pública 100%”.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El tribunal no lee el Canva',
          lead: 'Si las bases piden PDF simple y el tuyo es de dos columnas, el archivo puede ni abrirse bien. Sube el CV y regenera una columna de texto.',
          from: 'CV creativo de empresa',
          to: 'A4 con títulos oficiales',
          button: 'Limpiar el CV para la bolsa',
        },
        { type: 'h2', text: 'Empresa privada → proceso público' },
        {
          type: 'p',
          text: 'Quita jerga de startup que el baremo no entiende; deja hechos (equipo, presupuesto, público atendido). No afirmes que ClarCV “garantiza” una plaza. Si el proceso es 100% test y no piden CV, no gastes el archivo: estudia. Si piden ambos, el CV no sustituye los justificantes.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Pongo el DNI y el número de instancia?',
              a: 'En el formulario, sí. En el CV, solo si las bases lo piden. Ver qué no poner.',
            },
            {
              q: '¿Foto?',
              a: 'Solo si la convocatoria la exige. En España, en privado suele sobrar; en un proceso público manda el PDF de bases.',
            },
            {
              q: '¿Sois un centro de oposiciones?',
              a: 'No. Somos una herramienta para el archivo. Las bases y el temario son oficiales.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'CV for Spanish public exams (oposiciones): the file they asked for',
      description:
        'Merits, PDF, and the official form: what CV to send (and what not to invent). We are not the BOE. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'A CV does not pass a public exam. Sometimes it is not even the scored document: the notice asks for an official form, diplomas, and a merits table. People searching this usually have a corporate Canva and want to “adapt it for the Administration”. The risk is sending an infographic where they want a simple PDF, or a creative CV where they want numbered annexes. Read the notice. ClarCV is not the BOE and not an exam coach.',
        },
        {
          type: 'p',
          text: 'When they do ask for a CV (pools, interim posts, some regional processes, interviews after the exam): one column, exact dates, official diploma names, no photo unless asked, no ID number on the PDF if the form already has it. Sisters: [what not to put](/blog/que-no-poner-en-el-cv), [checklist](/blog/checklist-cv-antes-de-enviar), [PDF vs Word](/blog/cv-pdf-vs-word).',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com) makes the file parseable and A4. You match each line to the scoring table. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. We do not compute merit points.',
        },
        { type: 'h2', text: 'CV vs application form vs merits' },
        {
          type: 'p',
          text: 'Three different things. The application is the official form. Merits are courses, seniority, and titles as defined in the rules. The CV is a narrative summary sometimes asked “for the board” or for a hiring pool. Do not copy an academy’s marketing layout: copy the fields the rules ask for. If they want “CV on the official template”, that PDF wins, not a LinkedIn export.',
        },
        {
          type: 'ul',
          items: [
            'Titles: official name, institution, issue date. A “own” master is not an official master.',
            'Courses: hours and body. A 45-minute webinar rarely scores.',
            'Experience: month and year, employer, duties the syllabus or post recognises.',
            'No creative objectives or “public vocation 100%” bars.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'The board will not read the Canva',
          lead: 'If the rules ask for a simple PDF and yours is two-column, the file may not even open cleanly. Upload and regenerate one text column.',
          from: 'Creative corporate CV',
          to: 'A4 with official titles',
          button: 'Clean the CV for the pool',
        },
        { type: 'h2', text: 'Private sector → public process' },
        {
          type: 'p',
          text: 'Drop startup jargon the table does not understand; keep facts (team, budget, public served). Do not claim ClarCV “guarantees” a post. If the process is 100% test and they do not ask for a CV, do not spend the file: study. If they ask for both, the CV does not replace certificates.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Put ID and application number on the CV?',
              a: 'On the form, yes. On the CV, only if the rules ask. See what not to put.',
            },
            {
              q: 'Photo?',
              a: 'Only if the notice requires it. In Spanish private hiring it often should go; in a public process the rules PDF wins.',
            },
            {
              q: 'Are you an exam academy?',
              a: 'No. We are a file tool. The rules and syllabus are official.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'seguimiento-despues-de-enviar-cv',
    date: '2026-08-22',
    category: 'career',
    related: ['enviar-cv-por-email', 'checklist-cv-antes-de-enviar', 'cv-para-portales-empleo'],
    es: {
      title: 'Seguimiento después de enviar el CV (sin parecer spam)',
      description:
        'Cuándo escribir, qué asunto y qué no reenviar. Seguimiento a los 5–7 días, no al día siguiente. ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'El seguimiento después de enviar el CV no consiste en reenviar el mismo PDF al día siguiente. En España, un mail breve a los 5–7 días laborables (o el plazo que marcó el anuncio) es lo habitual. Antes, pareces ansioso. A la tercera, spam. Quien busca esto ya envió el archivo: el siguiente paso es un correo que se pueda responder en treinta segundos, no un segundo currículum.',
        },
        {
          type: 'p',
          text: 'Si aplicaste por portal (InfoJobs, LinkedIn Easy Apply), el seguimiento por email solo tiene sentido si tienes un contacto real. Si no, el estado de la candidatura en el portal es el canal. No abras un hilo a info@ de 200 personas. Hermanas: [enviar CV por email](/blog/enviar-cv-por-email), [checklist](/blog/checklist-cv-antes-de-enviar), [portales](/blog/cv-para-portales-empleo).',
        },
        {
          type: 'p',
          text: 'Antes de seguir, confirma que el PDF era el bueno (nombre de archivo, una columna, adjunto que abre). ClarCV (clarcv.com) sirve para eso, no para “perseguir” al recruiter. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        { type: 'h2', text: 'Asunto y cuerpo (ejemplos)' },
        {
          type: 'p',
          text: 'Asunto: “Candidatura [puesto] — [Nombre] — seguimiento” o responde al hilo original. Cuerpo: una frase de contexto (“envié el CV el 12”), una línea de encaje (“3 años en mostrador / el anuncio pedía turnos”), una pregunta cerrada (“¿siguen el proceso esta quincena?”), despedida. No repegues el CV salvo que te lo pidan o el primero rebotara.',
        },
        {
          type: 'ul',
          items: [
            'Un seguimiento. Dos solo si te dijeron “te escribimos en dos semanas” y pasaron tres.',
            'LinkedIn: InMail corto al recruiter de la vacante, no a toda la empresa.',
            'No llames a recepción el mismo día del envío “para ver si llegó”.',
            'Si rechazaron, no discutas el ATS. Pide feedback solo si hubo entrevista.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El seguimiento no arregla un PDF roto',
          lead: 'Si el primero no se podía parsear, el segundo mail tampoco. Sube el archivo, regenera plantilla y entonces escribe.',
          from: 'Reenviar el Canva',
          to: 'PDF limpio + mail de 5 líneas',
          button: 'Revisar el CV antes de insistir',
        },
        { type: 'h2', text: 'Plantilla corta' },
        {
          type: 'p',
          text: 'Hola [Nombre], el [fecha] envié mi CV para [puesto] (ref. [si hay]). Sigo interesado/a. En el archivo detallo [un logro de una línea]. ¿Me confirmáis si el proceso sigue activo? Gracias, [teléfono]. Adjunta de nuevo solo si el hilo es nuevo o el adjunto falló. El tono es el de [enviar por email](/blog/enviar-cv-por-email): humano, sin “estimado reclutador” de plantilla de 2014.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Y si no contestan nunca?',
              a: 'Es lo más frecuente. Cierra el ciclo a las dos semanas y aplica a otra. El silencio no se arregla con un PDF más pesado.',
            },
            {
              q: '¿WhatsApp al número del anuncio?',
              a: 'Solo si el anuncio lo ofrece. Un WhatsApp a un móvil personal sacado de Internet es mala señal.',
            },
            {
              q: '¿ClarCV envía el seguimiento?',
              a: 'No. Prepara el archivo. El correo lo escribes tú.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Follow-up after sending a CV (without looking like spam)',
      description:
        'When to write, which subject, and what not to resend. Follow up after 5–7 days, not the next morning. ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'A follow-up after sending a CV is not resending the same PDF the next day. In Spain, a short email after 5–7 working days (or the window the ad stated) is normal. Earlier looks anxious. A third time is spam. People searching this already sent the file: the next step is a reply that takes thirty seconds, not a second CV.',
        },
        {
          type: 'p',
          text: 'If you applied via a board (InfoJobs, Easy Apply), email follow-up only makes sense if you have a real contact. If not, the application status on the board is the channel. Do not open a thread to a 200-person info@ box. Sisters: [send CV by email](/blog/enviar-cv-por-email), [checklist](/blog/checklist-cv-antes-de-enviar), [job boards](/blog/cv-para-portales-empleo).',
        },
        {
          type: 'p',
          text: 'Before you follow up, confirm the PDF was the right one (filename, one column, attachment that opens). ClarCV (clarcv.com) is for that, not for chasing the recruiter. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        { type: 'h2', text: 'Subject and body (examples)' },
        {
          type: 'p',
          text: 'Subject: “[Role] application — [Name] — follow-up” or reply on the original thread. Body: one context line (“I sent the CV on the 12th”), one fit line (“3 years on the floor / the ad asked for shifts”), one closed question (“Are you still reviewing this fortnight?”), sign-off. Do not reattach the CV unless they ask or the first bounced.',
        },
        {
          type: 'ul',
          items: [
            'One follow-up. A second only if they said “we will write in two weeks” and three have passed.',
            'LinkedIn: a short InMail to the vacancy recruiter, not the whole company.',
            'Do not call reception the same day “to check it arrived”.',
            'If they rejected you, do not argue with ATS. Ask for feedback only after an interview.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Follow-up does not fix a broken PDF',
          lead: 'If the first file could not be parsed, a second email will not either. Upload, regenerate a template, then write.',
          from: 'Resend the Canva',
          to: 'Clean PDF + 5-line email',
          button: 'Check the CV before chasing',
        },
        { type: 'h2', text: 'A short template' },
        {
          type: 'p',
          text: 'Hi [Name], on [date] I sent my CV for [role] (ref. [if any]). I am still interested. The file includes [one-line proof]. Could you confirm whether the process is still active? Thanks, [phone]. Reattach only if this is a new thread or the file failed. Tone matches [email send](/blog/enviar-cv-por-email): human, not a 2014 “dear recruiter” template.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'What if they never reply?',
              a: 'That is the usual case. Close the loop after two weeks and apply elsewhere. Silence is not fixed by a heavier PDF.',
            },
            {
              q: 'WhatsApp the number on the ad?',
              a: 'Only if the ad offers it. WhatsApping a personal mobile scraped from the internet is a bad signal.',
            },
            {
              q: 'Does ClarCV send the follow-up?',
              a: 'No. It prepares the file. You write the email.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-mexico-formato',
    date: '2026-08-22',
    category: 'career',
    related: ['hoja-de-vida-o-curriculum', 'foto-en-el-cv', 'objetivos-profesionales-cv'],
    es: {
      title: 'Formato de CV en México: foto, RFC y objetivo',
      description:
        'Currículum mexicano: foto aún habitual, RFC/CURP solo si lo piden, y el objetivo que sobra. Cómo adaptar el PDF. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'El formato de CV en México se parece al español más de lo que prometen las plantillas “hoja de vida mexicana” de 2012. Se dice currículum o CV, no siempre hoja de vida. Sigue siendo más habitual la foto que en España. El RFC y la CURP no son un encabezado obligatorio en el PDF que envías a un ATS de multilatina: van al formulario o al alta de nómina cuando te los piden. Quien busca esto suele tener un archivo de España o de Colombia y quiere “mexicanizarlo” sin reescribir la trayectoria.',
        },
        {
          type: 'p',
          text: 'Mapa corto: foto si el mercado local o la vacante la esperan; datos sensibles fuera; objetivo profesional → [resumen](/blog/resumen-profesional-cv) o la guía de [objetivo](/blog/objetivos-profesionales-cv). Vocabulario de países: [hoja de vida vs currículum](/blog/hoja-de-vida-o-curriculum). Foto en general: [foto en el CV](/blog/foto-en-el-cv).',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com) no es un despacho laboral mexicano. Regenera un A4 parseable. Tú quitas RFC si no toca y dejas keywords de la vacante. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        { type: 'h2', text: 'Qué quitar del PDF (y qué dejar)' },
        {
          type: 'ul',
          items: [
            'RFC, CURP, NSS, estado civil, hijos: fuera del CV salvo que el anuncio o el portal los pidan en un campo aparte.',
            'Dirección completa y colonia: ciudad y zona basta (CDMX, GDL, MTY).',
            'Objetivo: “busco empleo en una empresa líder” no suma. Un perfil de 3 líneas sí.',
            'Foto: en muchos procesos locales aún se espera; en filiales US/EU y ATS globales, el archivo sin foto parsea más limpio. Ten dos versiones si hace falta.',
            'Sueldo pretendido: no lo pongas en el CV; va al formulario si lo piden.',
          ],
        },
        {
          type: 'p',
          text: 'Antes: un Word de dos páginas con foto oval, RFC, objetivo de seis líneas y “paquete de Office”. Después: una columna, ciudad, LinkedIn, perfil con sector y un número, experiencia con logros, Office desglosado (Excel avanzado, no “paquete”). El ATS de OCC, Computrabajo o el Workday de una maquila busca texto, no el escudo de la prepa.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El RFC no es una keyword de puesto',
          lead: 'Si el PDF abre con CURP y un objetivo vago, el filtro pierde el cargo. Sube el archivo y deja perfil y experiencia en texto.',
          from: 'RFC + objetivo 2012',
          to: 'Perfil + logros parseables',
          button: 'Adaptar mi CV a México',
        },
        { type: 'h2', text: 'De España o Colombia a una vacante MX' },
        {
          type: 'p',
          text: 'Cambia vocabulario (currículum, no solo hoja de vida; “licenciatura” si aplica). Fechas en mes/año. Inglés: MCER o “TOEFL” si lo tienes, no “inglés 90%”. No copies un sueldo en euros. Si el archivo es Canva de dos columnas, aplana primero: el parser mexicano no es más mágico que el español.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Obligatorio poner foto en México?',
              a: 'No hay una ley que te obligue en todo proceso. En práctica local, muchos reclutadores la esperan. En ATS global, a menudo sobra. Dos PDFs resuelven el dilema.',
            },
            {
              q: '¿Pongo la universidad y el promedio?',
              a: 'Universidad sí. Promedio solo si es alto o lo piden (becas, trainee).',
            },
            {
              q: '¿ClarCV cumple NOM o IMSS?',
              a: 'No. Es una herramienta de documento. Alta y contratos son de la empresa.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Mexico CV format: photo, RFC, and the old objective',
      description:
        'Mexican resume: photo still common, RFC/CURP only if asked, and the objective you can drop. How to adapt the PDF. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'A CV format for Mexico is closer to Spain than 2012 “Mexican hoja de vida” templates suggest. People say currículum or CV, not always hoja de vida. A photo is still more common than in Spain. RFC and CURP are not a mandatory header on the PDF you send to a multinational ATS: they go on the form or payroll when asked. People searching this usually have a Spain or Colombia file and want to “Mexicanise” it without rewriting their career.',
        },
        {
          type: 'p',
          text: 'Short map: photo if the local market or vacancy expects it; sensitive IDs off the PDF; professional objective → [summary](/blog/resumen-profesional-cv) or the [objective](/blog/objetivos-profesionales-cv) guide. Country names: [hoja de vida vs CV](/blog/hoja-de-vida-o-curriculum). Photos in general: [photo on the CV](/blog/foto-en-el-cv).',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com) is not a Mexican labour firm. It regenerates a parseable A4. You remove RFC if it does not belong and keep vacancy keywords. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        { type: 'h2', text: 'What to strip (and what to keep)' },
        {
          type: 'ul',
          items: [
            'RFC, CURP, social-security number, marital status, children: off the CV unless the ad or portal asks in a separate field.',
            'Full street and neighbourhood: city and area is enough (Mexico City, GDL, MTY).',
            'Objective: “I seek a job at a leading company” adds nothing. A 3-line profile does.',
            'Photo: many local processes still expect it; US/EU subsidiaries and global ATS parse cleaner without it. Keep two versions if needed.',
            'Salary expectation: not on the CV; use the form if they ask.',
          ],
        },
        {
          type: 'p',
          text: 'Before: a two-page Word with an oval photo, RFC, a six-line objective, and “Microsoft Office”. After: one column, city, LinkedIn, a profile with sector and one number, experience with outcomes, Office split (advanced Excel, not “suite”). OCC, Computrabajo, or a plant Workday wants text, not a high-school crest.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'RFC is not a job keyword',
          lead: 'If the PDF opens with CURP and a vague objective, the filter misses the role. Upload and keep profile plus experience in text.',
          from: 'RFC + 2012 objective',
          to: 'Parseable profile and wins',
          button: 'Adapt my CV for Mexico',
        },
        { type: 'h2', text: 'From Spain or Colombia to a Mexican vacancy' },
        {
          type: 'p',
          text: 'Switch vocabulary (currículum, not only hoja de vida; “licenciatura” if it applies). Month/year dates. English: CEFR or TOEFL if you have it, not “English 90%”. Do not paste a salary in euros. If the file is two-column Canva, flatten first: a Mexican parser is not more magical than a Spanish one.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Is a photo mandatory in Mexico?',
              a: 'No single law forces it on every process. In local practice many recruiters expect it. On a global ATS it often should go. Two PDFs solve the split.',
            },
            {
              q: 'University and GPA?',
              a: 'University yes. GPA only if it is strong or they ask (grants, trainee).',
            },
            {
              q: 'Does ClarCV cover Mexican labour rules?',
              a: 'No. It is a document tool. Hiring and contracts are the employer’s.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'objetivos-profesionales-cv',
    date: '2026-08-22',
    category: 'writing',
    related: ['resumen-profesional-cv', 'adaptar-cv-a-una-oferta', 'cv-mexico-formato'],
    es: {
      title: 'Objetivo profesional en el CV: quitarlo o reescribirlo',
      description:
        '“Busco un puesto que me permita crecer” sobra. Sustituye el objetivo por un resumen de 3 líneas. ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'El objetivo profesional en el CV (“busco incorporarme a una empresa líder donde pueda desarrollarme”) es una costumbre de plantillas viejas, aún viva en México, Colombia y en Word de instituto. En España y en ATS europeos, ese bloque ocupa el sitio del [resumen profesional](/blog/resumen-profesional-cv) y no aporta keywords de oficio. Quien busca esto suele dudar si borrarlo. Casi siempre: bórralo o conviértelo en perfil.',
        },
        {
          type: 'p',
          text: 'Excepción estrecha: primer empleo o cambio de sector, una frase de dirección más una prueba (“Graduada en ADE; busco analista junior; Excel y 6 meses de prácticas en reporting”). Eso ya no es un objetivo vacío: es un pitch. No clones la guía del resumen: aquí decides si el bloque que ya tienes se queda, se funde o se va.',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com) no debe inventar un objetivo poético. Si el archivo trae uno vago, el análisis suele marcarlo como ruido. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        { type: 'h2', text: 'Por qué el objetivo pierde el screening' },
        {
          type: 'p',
          text: 'El ATS indexa palabras de oficio. “Crecer”, “reto” y “empresa líder” están en diez mil PDFs y en cero descripciones de puesto. El reclutador lee el bloque en dos segundos y no sabe qué has hecho. El resumen dice rol, sector y una cifra. El objetivo dice un deseo. En un portal, el deseo sobra.',
        },
        {
          type: 'ul',
          items: [
            'Bórralo si ya tienes experiencia: el primer puesto ya explica la dirección.',
            'Fúndelo en Perfil / Resumen si eres junior: dirección + herramienta + prueba.',
            'No lo dejes en un recuadro con un icono de brújula: el parser no extrae “objetivo”.',
            'Si aplicas en México y el reclutador local aún lo espera, que sea de 2 líneas con oficio, no un párrafo motivacional. Ver [formato México](/blog/cv-mexico-formato).',
          ],
        },
        {
          type: 'p',
          text: 'Antes: “Objetivo: formar parte de un equipo multidisciplinar donde aportar mis conocimientos y seguir aprendiendo.” Después: “Analista junior (prácticas en reporting, Excel, Power BI). Busco puesto de analista de datos en retail. SQL básico; dashboard de ventas quincenal.” Misma persona. Solo el segundo tiene keywords.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '“Busco crecer” no es un puesto',
          lead: 'Si el PDF abre con un objetivo de plantilla, el filtro pierde el oficio. Sube el archivo y deja un resumen de tres líneas en texto.',
          from: 'Objetivo de Word 2012',
          to: 'Perfil con rol y prueba',
          button: 'Sustituir el objetivo',
        },
        { type: 'h2', text: 'Cómo sustituirlo en el archivo que ya tienes' },
        {
          type: 'p',
          text: 'Cambia el encabezado a Resumen o Perfil. Copia una keyword de la oferta solo si la tienes: [adaptar a la oferta](/blog/adaptar-cv-a-una-oferta). Quita adjetivos (proactivo, apasionado). Si el objetivo era la única intro, no dejes el CV sin bloque superior: el ATS a veces puntúa ese párrafo. Tres líneas bastan. Luego ClarCV para que el encabezado sea texto estándar, no un text box de Canva.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿En LatAm todavía lo piden?',
              a: 'Algunos reclutadores locales sí lo esperan. Que sea corto y con oficio. Un párrafo de “superarme día a día” no.',
            },
            {
              q: '¿Objetivo y resumen a la vez?',
              a: 'No. Es el mismo sitio en la página. Elige resumen.',
            },
            {
              q: '¿ClarCV escribe un objetivo nuevo?',
              a: 'No debería. Conserva hechos y formatea un perfil. Tú borras la frase vacía.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Career objective on a CV: delete it or rewrite it',
      description:
        '“I seek a role where I can grow” is wasted space. Replace the objective with a 3-line summary. ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'A career objective on a CV (“I seek to join a leading company where I can develop”) is an old-template habit, still alive in Mexico, Colombia, and school Word files. In Spain and European ATS tools, that block takes the place of the [professional summary](/blog/resumen-profesional-cv) and adds no job keywords. People searching this are usually unsure whether to delete it. Almost always: delete it or turn it into a profile.',
        },
        {
          type: 'p',
          text: 'Narrow exception: first job or career change — one direction sentence plus proof (“BA in Business; targeting junior analyst; Excel and 6 months reporting internship”). That is no longer an empty objective: it is a pitch. Do not clone the summary guide: here you decide whether the block you already have stays, merges, or goes.',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com) should not invent a poetic objective. If the file has a vague one, analysis often flags it as noise. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        { type: 'h2', text: 'Why the objective loses the screen' },
        {
          type: 'p',
          text: 'ATS indexes job words. “Grow”, “challenge”, and “leading company” sit in ten thousand PDFs and in zero job descriptions. The recruiter reads the block in two seconds and still does not know what you have done. A summary states role, sector, and a number. An objective states a wish. On a job board, the wish is spare.',
        },
        {
          type: 'ul',
          items: [
            'Delete it if you already have experience: the first job already shows direction.',
            'Merge it into Profile / Summary if you are junior: direction + tool + proof.',
            'Do not leave it in a box with a compass icon: the parser does not extract “objective”.',
            'If you apply in Mexico and a local recruiter still expects it, keep 2 lines with a job title, not a motivational paragraph. See [Mexico format](/blog/cv-mexico-formato).',
          ],
        },
        {
          type: 'p',
          text: 'Before: “Objective: join a multidisciplinary team where I can contribute my knowledge and keep learning.” After: “Junior analyst (reporting internship, Excel, Power BI). Targeting a retail data-analyst role. Basic SQL; fortnightly sales dashboard.” Same person. Only the second has keywords.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '“I want to grow” is not a job title',
          lead: 'If the PDF opens with a template objective, the filter misses the craft. Upload the file and leave a three-line summary in text.',
          from: '2012 Word objective',
          to: 'Profile with role and proof',
          button: 'Replace the objective',
        },
        { type: 'h2', text: 'How to replace it in the file you already have' },
        {
          type: 'p',
          text: 'Change the heading to Summary or Profile. Copy a keyword from the ad only if you have it: [tailor to the job](/blog/adaptar-cv-a-una-oferta). Drop adjectives (proactive, passionate). If the objective was the only intro, do not leave the CV with no top block: ATS sometimes scores that paragraph. Three lines are enough. Then ClarCV so the heading is standard text, not a Canva text box.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Do they still ask for it in LatAm?',
              a: 'Some local recruiters still expect it. Keep it short and job-titled. A “improve every day” paragraph, no.',
            },
            {
              q: 'Objective and summary together?',
              a: 'No. It is the same slot on the page. Pick summary.',
            },
            {
              q: 'Will ClarCV write a new objective?',
              a: 'It should not. It keeps facts and formats a profile. You delete the empty sentence.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
];
 